import React, { useState, useCallback, useEffect, useRef } from 'react';
import MapGL, { NavigationControl, FullscreenControl, ScaleControl, Popup, Source, Layer } from 'react-map-gl';
import moment from 'moment';
import './AggregationMapView.css';
import { useApi } from '../../hooks';
import Pin from '../../components/Pin';
import LocationDetails from './LocationDetails/LocationDetails';
import QuoteModal from './QuoteModal/QuoteModal';
import { ActivityIndicator, Box, Button, Checkbox, DatePicker, MenuItem, Select, Text, TextField, Grid } from '../../ui';
import { Dialog, LayoutBox } from '../../components';
import RunReports from './RunReports/RunReports';
import { LocationDialog } from '../../pages/SubmitApplicationPage/LocationDialog';
const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const OVERLAY_QUOTE_ID = '16d7310b-3a29-41a8-bba1-cbb32d580bbe';

const createRadiusRing = (center, radiusMiles) => {
  const earthRadius = 3963.2;
  const lat = center[1];
  const lng = center[0];
  const points = [];

  for (let i = 0; i <= 360; i++) {
    const angle = (i * Math.PI) / 180;
    const lat2 = lat + (radiusMiles / earthRadius) * (180 / Math.PI) * Math.cos(angle);
    const lng2 = lng + ((radiusMiles / earthRadius) * (180 / Math.PI) * Math.sin(angle)) / Math.cos((lat * Math.PI) / 180);
    points.push([lng2, lat2]);
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [points],
    },
  };
};
const getLastQuarterEndDate = () => {
  const now = moment();
  const month = now.month();
  const year = now.year();

  if (month < 2) {
    return moment([year - 1, 11, 31]);
  } else if (month < 5) {
    return moment([year, 2, 31]);
  } else if (month < 8) {
    return moment([year, 5, 30]);
  } else if (month < 11) {
    return moment([year, 8, 30]);
  } else {
    return moment([year, 8, 30]);
  }
};

const AggregationMapView = () => {
  const [viewport, setViewport] = useState({
    latitude: 37.785164,
    longitude: -100,
    zoom: 3.5,
    bearing: 0,
    pitch: 0,
  });
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [gridReference, setGridReference] = useState(null);
  const [aggregationLocations, setAggregationLocations] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [aggregationQuotes, setAggregationQuotes] = useState([]);
  const [RunReportsModalOpen, setRunReportsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('pif');
  const [activePoliciesDate, setActivePoliciesDate] = useState(getLastQuarterEndDate());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    getAggregationLocations,
    getAggregationConfigurations,
    getAggregationQuotes,
    getAggregationLocationsForPIF,
    addOrUpdateCustomerLocation,
    getoverlayQuoteLocations,
    getOverlayOnlyLocations,
  } = useApi();
  const [isOverlayLocationOpen, setIsOverlayLocationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingOverlayLocations, setPendingOverlayLocations] = useState([]);
  const [shouldResetBatch, setShouldResetBatch] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [pendingLocationToSave, setPendingLocationToSave] = useState(null);
  const [batchStarted, setBatchStarted] = useState(false);
  const [isPlottingOverlay, setIsPlottingOverlay] = useState(false);

  const [selectedConfig, setSelectedConfig] = useState(null);
  const [overlayLocations, setOverlayLocations] = useState([]);
  const [isOverlayLocationsOpen, setIsOverlayLocationsOpen] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [showOverlayOnly, setShowOverlayOnly] = useState(false);

  const pifLoadedRef = useRef(false);
  const configLoadedRef = useRef(false);
  const overlayLoadedRef = useRef(false);

  const fetchData = async () => {
    if (configLoadedRef.current) return;
    configLoadedRef.current = true;
    try {
      const configs = await getAggregationConfigurations();
      configLoadedRef.current = true;
      if (configs && configs.length > 0) {
        const sortedConfigurations = configs.sort((a, b) => {
          const dateA = moment.utc(a.updatedAt).format('MM-DD-YYYY');
          const dateB = moment.utc(b.updatedAt).format('MM-DD-YYYY');
          return moment(dateB, 'MM-DD-YYYY').diff(moment(dateA, 'MM-DD-YYYY'));
        });
        setConfigurations(sortedConfigurations);
      }

      await loadPIFLocations();
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const loadPIFLocations = useCallback(async () => {
    if (pifLoadedRef.current) return;
    pifLoadedRef.current = true;
    setIsLoading(true);
    try {
      const locations = await getAggregationLocationsForPIF(activePoliciesDate.valueOf(), OVERLAY_QUOTE_ID);
      setAggregationLocations(locations || []);
      pifLoadedRef.current = true;
    } catch (error) {
      console.error('Error fetching PIF locations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [activePoliciesDate, getAggregationLocationsForPIF]);
  useEffect(() => {
    if (viewMode === 'pif') {
      loadPIFLocations();
    }
  }, [viewMode]);

  useEffect(() => {
    if (viewMode === 'pif') {
      pifLoadedRef.current = false;
      loadPIFLocations();
    }
  }, [viewMode, activePoliciesDate]);

  const recentConfig = configurations && configurations[0];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (viewMode !== 'overlaySingle') {
      setSelectedLocations([]);
    }
  }, [viewMode]);

  const refreshRunReportData = async () => {
    try {
      const fresh = await getAggregationLocationsForPIF(activePoliciesDate.valueOf(), OVERLAY_QUOTE_ID);
      setAggregationLocations(fresh || []);
    } catch (err) {
      console.error('Failed to refresh run report data', err);
    }
  };
  const handleOverlayLocationsClick = async () => {
    setIsButtonLoading(true);
    try {
      const locations = await getoverlayQuoteLocations(OVERLAY_QUOTE_ID);
      setOverlayLocations(locations || []);
      setIsOverlayLocationsOpen(true);
    } catch (error) {
      console.error('Failed to fetch overlay locations', error);
    } finally {
      setIsButtonLoading(false);
    }
  };

  const handleConfigChange = useCallback(
    async (config) => {
      if (!config) return;

      setSelectedConfig(config);

      if (config?.aggregationId) {
        try {
          const quotes = await getAggregationQuotes(config.aggregationId);
          setAggregationQuotes(quotes || []);
        } catch (error) {
          console.error('Error fetching quotes:', error);
        }
      }
    },
    [getAggregationQuotes]
  );
  useEffect(() => {
    if (viewMode !== 'overlaySingle') return;

    const loadOverlayQuoteLocations = async () => {
      try {
        const locations = await getoverlayQuoteLocations(OVERLAY_QUOTE_ID);
        setPendingOverlayLocations(locations || []);
      } catch (error) {
        console.error('Failed to load overlay quote locations', error);
        setPendingOverlayLocations([]);
      }
    };

    loadOverlayQuoteLocations();
  }, [viewMode, getoverlayQuoteLocations]);

  // Removed automatic loading when switching to 'overlay' mode
  // Locations are now only loaded when "Map Configuration" is clicked (handleMapConfiguration)

  const handleMapConfiguration = useCallback(
    async (config, activePoliciesDate) => {
      const configuration = config || recentConfig;
      if (configuration?.aggregationId) {
        try {
          const locations = await getAggregationLocations(
            configuration?.aggregationId,
            activePoliciesDate ? activePoliciesDate : activePoliciesDate.valueOf(),
            { quoteId: OVERLAY_QUOTE_ID }
          );
          setAggregationLocations(locations || []);
          setIsQuoteModalOpen(false);
        } catch (error) {
          console.error('Error fetching locations:', error);
        }
      }
    },
    [getAggregationLocations, recentConfig, activePoliciesDate]
  );

  const handlePinClick = (location) => {
    setSelectedLocations([location]);
  };

  const ButtonLoadingIndicator = React.forwardRef((props, ref) => {
    const { color, size, ...rest } = props;
    return (
      <Box {...rest} ref={ref}>
        <ActivityIndicator color={color || '$primary'} size={size} />
      </Box>
    );
  });

  const handleRowSelect = (locations, centerLocation) => {
    setSelectedLocations(locations);

    if (centerLocation) {
      setViewport({
        ...viewport,
        latitude: centerLocation.lat,
        longitude: centerLocation.lon,
        zoom: 8,
        transitionDuration: 1000,
      });
    }
  };

  const handleSetQuotesClick = () => {
    setIsQuoteModalOpen(true);
  };
  const saveOverlayLocation = useCallback(
    async (locationData, resetFlag) => {
      setIsSubmitting(true);
      try {
        const response = await addOrUpdateCustomerLocation(OVERLAY_QUOTE_ID, locationData, resetFlag, true);

        const savedLoc = response?.data || response;
        return savedLoc;
      } catch (error) {
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [addOrUpdateCustomerLocation]
  );

  const renderRadiusRings = () => {
    if (!selectedLocations || selectedLocations.length === 0) return null;

    return selectedLocations.map((location, locationIndex) => {
      const center = [location.lon, location.lat];
      const radii = [5, 10, 25, 50];

      return radii.map((radius, index) => {
        const ring = createRadiusRing(center, radius);
        return (
          <Source key={`ring-${locationIndex}-${radius}`} type="geojson" data={ring}>
            <Layer
              id={`ring-${locationIndex}-${radius}`}
              type="line"
              paint={{
                'line-color': index === 0 ? '#FF0000' : index === 1 ? '#FFA500' : index === 2 ? '#00FF00' : '#00e1ff',
                'line-width': 2,
                'line-opacity': 0.8,
              }}
            />
          </Source>
        );
      });
    });
  };

  const renderPins = () => {
    const baseLocations =
      viewMode === 'pif' ? aggregationLocations.filter((location) => location.attributes?.type !== 'quote') : aggregationLocations;

    // When showOverlayOnly is true, show all locations returned from the API (no filtering needed)
    const filteredLocations = showOverlayOnly ? aggregationLocations : baseLocations;

    // Filter out locations without valid coordinates
    const locationsWithCoords = filteredLocations.filter(
      (location) => location.lat != null && location.lon != null && !isNaN(location.lat) && !isNaN(location.lon)
    );

    const locationGroups = {};
    locationsWithCoords.forEach((location) => {
      const key = `${location.lat}-${location.lon}`;
      if (!locationGroups[key]) locationGroups[key] = [];
      locationGroups[key].push(location);
    });

    const pins = [];

    Object.values(locationGroups).forEach((group) => {
      const count = group.length;

      group.forEach((location, index) => {
        const radius = 0.0005;
        const angle = (index / count) * 2 * Math.PI;

        const offsetLat = location.lat + radius * Math.cos(angle);
        const offsetLon = location.lon + radius * Math.sin(angle);

        const isPolicy = location.attributes?.type === 'policy' || location.attributes?.type === 'policy endorsement';

        pins.push(
          <Pin
            key={`${location.id}-${index}`}
            latitude={offsetLat}
            longitude={offsetLon}
            type={location.attributes?.type === 'overlay' ? 'overlay' : isPolicy ? 'policy' : 'quote'}
            onMouseEnter={() => setHoveredLocation(location)}
            onMouseLeave={() => setHoveredLocation(null)}
            onClick={() => handlePinClick(location)}
          />
        );
      });
    });

    return pins;
  };

  return (
    <LayoutBox flex={1} position="relative">
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 90,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Select
          value={viewMode}
          onChangeValue={setViewMode}
          styles={{
            textInput: {
              backgroundColor: '#ffffffff',
              border: '2px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '6px',
              padding: '8px 12px',
              minWidth: '180px',
            },
            input: {
              color: '#000000',
              fontWeight: 400,
              fontSize: '18px',
            },
            menuItem: {
              padding: '8px 12px',
              fontSize: '14px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
              },
            },
          }}
        >
          <MenuItem value="pif">PIF Only</MenuItem>
          <MenuItem value="overlay">Overlay Quotes</MenuItem>
          <MenuItem value="overlaySingle">Configure overlay quote</MenuItem>
        </Select>

        {viewMode === 'overlaySingle' && (
          <Button label="Overlay Quote Locations" onPress={handleOverlayLocationsClick} loading={isButtonLoading} sx={{ height: '100%' }} />
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          maxHeight: '60px',
          position: 'absolute',
          top: 16,
          left: 380,
          zIndex: 999,
          flexDirection: 'row',
          alignItems: 'center',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          backgroundColor: '#ffffff',
          gap: 3,
          minWidth: 0,
        }}
      >
        <Text sx={{ justifyContent: 'end', fontWeight: 400, fontSize: '18px', color: '$primary', padding: 10 }}>Active Policies as of:</Text>
        {isLoading ? (
          <Box sx={{ width: 130, height: 60, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <ButtonLoadingIndicator size="small" color="$primary" />
          </Box>
        ) : (
          <>
            <TextField
              value={activePoliciesDate.format('MM/DD/YYYY')}
              readOnly
              onPress={() => setDatePickerOpen(true)}
              sx={{ position: 'relative', width: 130, cursor: 'pointer', height: 60 }}
            />
            {viewMode !== 'pif' && selectedConfig && (
              <Text sx={{ justifyContent: 'end', fontWeight: 400, fontSize: '18px', color: '$primary', padding: 10 }}>{selectedConfig.name}</Text>
            )}

            <Dialog open={datePickerOpen} onClose={() => setDatePickerOpen(false)}>
              <DatePicker
                date={activePoliciesDate}
                onChange={(date) => {
                  setActivePoliciesDate(moment(date));
                  setDatePickerOpen(false);
                }}
              />
            </Dialog>
          </>
        )}
      </Box>

      <Box sx={{ position: 'absolute', top: 16, right: 200, zIndex: 1, display: 'flex' }}>
        <Button onPress={() => setRunReportsModalOpen(true)}>Run Reports</Button>
      </Box>
      {viewMode === 'overlay' && (
        <Box sx={{ position: 'absolute', top: 16, right: 320, zIndex: 1, display: 'flex', gap: 12 }}>
          <Button onPress={handleSetQuotesClick}>Set Quotes</Button>
          <Button
            onPress={async () => {
              setIsButtonLoading(true);
              try {
                const overlayOnlyLocations = await getOverlayOnlyLocations(OVERLAY_QUOTE_ID);

                // Debug: log location types
                if (overlayOnlyLocations && overlayOnlyLocations.length > 0) {
                  const typeCounts = overlayOnlyLocations.reduce((acc, loc) => {
                    const type = loc.attributes?.type || 'unknown';
                    acc[type] = (acc[type] || 0) + 1;
                    return acc;
                  }, {});
                  console.log('Overlay-only locations by type:', typeCounts);
                  console.log('Total locations:', overlayOnlyLocations.length);

                  // Check for locations without valid coordinates
                  const withoutCoords = overlayOnlyLocations.filter((loc) => !loc.lat || !loc.lon);
                  if (withoutCoords.length > 0) {
                    console.log('Locations without valid coordinates:', withoutCoords.length);
                  }
                }

                setAggregationLocations(overlayOnlyLocations || []);
                setShowOverlayOnly(true);
                setSelectedLocations([]);
              } catch (error) {
                console.error('Failed to fetch overlay quote locations', error);
              } finally {
                setIsButtonLoading(false);
              }
            }}
            loading={isButtonLoading}
          >
            Add Overlay Quote Only
          </Button>
        </Box>
      )}

      {viewMode === 'overlaySingle' && (
        <Box sx={{ position: 'absolute', top: 16, right: 320, zIndex: 1, display: 'flex' }}>
          <Button label="Add Overlay Location" onPress={() => setIsOverlayLocationOpen(true)} />
        </Box>
      )}

      <Box sx={{ height: '100vh', position: 'relative' }}>
        <MapGL
          {...viewport}
          width="100%"
          height="100%"
          mapStyle="mapbox://styles/hewitte/cjwaq462118se1cpas6fme42m"
          onViewportChange={setViewport}
          mapboxApiAccessToken={TOKEN}
          onClick={() => {
            if (viewMode !== 'overlaySingle') {
              setSelectedLocations([]);
            }
          }}
        >
          {renderPins()}
          {renderRadiusRings()}
          {hoveredLocation && hoveredLocation.aggregates && (
            <Popup
              longitude={hoveredLocation.lon}
              latitude={hoveredLocation.lat}
              closeButton={false}
              closeOnClick={false}
              anchor="top"
              offsetTop={20}
            >
              <LocationDetails aggregates={hoveredLocation.aggregates} location={hoveredLocation} />
            </Popup>
          )}
          <Box className="map-controls fullscreen">
            <FullscreenControl />
          </Box>
          <Box className="map-controls navigation">
            <NavigationControl />
          </Box>
          <Box className="map-controls scale">
            <ScaleControl />
          </Box>
        </MapGL>
      </Box>
      {isQuoteModalOpen && (
        <QuoteModal
          isOpen={isQuoteModalOpen}
          onClose={() => setIsQuoteModalOpen(false)}
          setGridReference={setGridReference}
          onConfigChange={handleConfigChange}
          configurations={configurations}
          aggregationQuotes={aggregationQuotes}
          onMapConfig={handleMapConfiguration}
          activePoliciesDate={activePoliciesDate}
          onActivePoliciesDateChange={setActivePoliciesDate}
        />
      )}
      {RunReportsModalOpen && (
        <RunReports
          isOpen={RunReportsModalOpen}
          onClose={() => setRunReportsModalOpen(false)}
          aggregationLocations={aggregationLocations}
          onRowSelect={handleRowSelect}
          recentConfig={recentConfig}
          activePoliciesDate={activePoliciesDate}
          viewMode={viewMode}
          overlayQuoteId={OVERLAY_QUOTE_ID}
        />
      )}
      <LocationDialog
        open={isOverlayLocationOpen}
        pendingOverlayLocations={pendingOverlayLocations}
        setPendingOverlayLocations={setPendingOverlayLocations}
        onClose={() => {
          setIsOverlayLocationOpen(false);
          setShouldResetBatch(null);
          setShowResetConfirm(false);
          setPendingLocationToSave(null);
          setBatchStarted(false);
        }}
        mode="CREATE"
        canEdit={true}
        quoteId={OVERLAY_QUOTE_ID}
        saveButtonLabel={pendingOverlayLocations.length === 0 ? 'Add Location' : 'Add Another Location'}
        processing={isSubmitting}
        plottingProcessing={isPlottingOverlay}
        shouldAddRiskId={false}
        data={{
          details: { num_vehicles: 1, averageCarValue: 0 },
        }}
        showPlotOverlayButton={true}
        pendingLocationsCount={pendingOverlayLocations.length}
        onPlotOverlay={async () => {
          setIsPlottingOverlay(true);
          try {
            const aggregationId = recentConfig?.aggregationId;
            if (!aggregationId) throw new Error('Aggregation ID not found');

            const newOverlayLocs = [];

            for (const location of pendingOverlayLocations) {
              const aggData = await getAggregationLocations(aggregationId, activePoliciesDate.valueOf(), {
                quoteId: OVERLAY_QUOTE_ID,
                locationId: location.location_id || location.id,
              });

              const matched = aggData.find((loc) => Math.abs(loc.lat - location.lat) < 0.0001 && Math.abs(loc.lon - location.lon) < 0.0001);

              const overlayLoc = {
                id: location.location_id || location.id || Date.now() + Math.random(),
                name: location.name,
                lat: location.lat,
                lon: location.lon,
                coveredValue: location.coveredValue,
                address: location.address,
                attributes: {
                  type: 'singleLocation',
                },
                aggregates: matched?.aggregates || {
                  coverage5mi: 0,
                  coverage10mi: 0,
                  coverage25mi: 0,
                },
              };

              newOverlayLocs.push(overlayLoc);
            }
            setAggregationLocations((prev) => {
              const existingIds = new Set(prev.map((loc) => loc.id));
              const merged = [...prev, ...newOverlayLocs.filter((loc) => !existingIds.has(loc.id))];
              return merged;
            });

            setSelectedLocations(newOverlayLocs);

            if (newOverlayLocs.length > 0) {
              setViewport((prev) => ({
                ...prev,
                latitude: newOverlayLocs[0].lat,
                longitude: newOverlayLocs[0].lon,
                zoom: 8,
                transitionDuration: 1000,
              }));
            }

            setIsOverlayLocationOpen(false);
            setPendingOverlayLocations([]);
            setShouldResetBatch(null);
            setShowResetConfirm(false);
            setPendingLocationToSave(null);
            await refreshRunReportData();
          } catch (error) {
            alert('Failed to plot overlay locations.');
          } finally {
            setIsPlottingOverlay(false);
          }
        }}
        onSubmit={async (newLocation) => {
          try {
            const patchedLocation = {
              numVehicles: newLocation.numVehicles ?? 1,
              averageCarValue: newLocation.averageCarValue ?? 0,
              ...newLocation,
            };

            const locationData = {
              name: patchedLocation.name || patchedLocation.address || 'Untitled Location',
              placeId: patchedLocation.placeId || '',
              address: patchedLocation.address,
              lat: Number(patchedLocation.lat) || 0,
              lon: Number(patchedLocation.lon) || 0,
              coveredValue: Number(patchedLocation.coveredValue) || 0,
              countryCode: patchedLocation.countryCode || '',
              regionCode: patchedLocation.regionCode || '',
              city: patchedLocation.city || '',
              postcode: patchedLocation.postcode || '',
            };

            if (!batchStarted && shouldResetBatch === null) {
              setPendingLocationToSave(locationData);
              setShowResetConfirm(true);
              return;
            }

            const resetFlag = !batchStarted && shouldResetBatch === true;

            const saved = await saveOverlayLocation(locationData, resetFlag);

            if (resetFlag) {
              setPendingOverlayLocations([saved]);
            } else {
              setPendingOverlayLocations((prev) => [...prev, saved]);
            }

            if (!batchStarted) {
              setBatchStarted(true);
            }
          } catch (error) {
            alert('Failed to add new overlay location.');
          }
        }}
      />

      {showResetConfirm && (
        <Dialog open={showResetConfirm} onClose={() => setShowResetConfirm(false)}>
          <Box padding={16}>
            <Text sx={{ fontSize: 16, marginBottom: 12 }}>Do you want to delete the previous batch of overlay locations before adding new ones?</Text>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                onPress={async () => {
                  setShouldResetBatch(false);
                  setShowResetConfirm(false);
                  if (!pendingLocationToSave) return;

                  try {
                    const saved = await saveOverlayLocation(pendingLocationToSave, false);
                    setPendingOverlayLocations((prev) => [...prev, saved]);
                    setPendingLocationToSave(null);
                    setBatchStarted(true);
                  } catch (err) {
                    alert('Failed to add location. Please try again.');
                  }
                }}
              >
                No
              </Button>
              <Button
                variant="primary"
                onPress={async () => {
                  if (!pendingLocationToSave) return;

                  setShowResetConfirm(false);
                  setShouldResetBatch(true);

                  try {
                    const saved = await saveOverlayLocation(pendingLocationToSave, true);

                    setPendingOverlayLocations([saved]);
                    setPendingLocationToSave(null);
                    setBatchStarted(true);
                  } catch (err) {
                    alert('Failed to reset and add location. Please check devtools for details.');
                  }
                }}
              >
                Yes, delete previous batch
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}

      {isSubmitting && (
        <Box
          sx={{
            zIndex: 9999,
            position: 'fixed',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.6)',
            display: 'flex',
          }}
        >
          <ActivityIndicator color="$primary" size="large" />
        </Box>
      )}

      <Dialog
        heading="Overlay Quote Locations"
        open={isOverlayLocationsOpen}
        onClose={() => setIsOverlayLocationsOpen(false)}
        actions={<Button label="Close" onPress={() => setIsOverlayLocationsOpen(false)} />}
        styles={{
          dialogBox: {
            width: '1000px',
            maxWidth: '95%',
            height: '700px',
            maxHeight: '90vh',
          },
        }}
      >
        <div style={{ height: '100%', width: '100%' }}>
          <Grid
            data={overlayLocations}
            setGridReference={() => {}}
            columns={[
              { headerName: 'Name', field: 'name', flex: 1, minWidth: 150 },
              { headerName: 'Address', field: 'address', flex: 3, minWidth: 300 },
              { headerName: 'City', field: 'city', flex: 1, minWidth: 120 },
              { headerName: 'Postcode', field: 'postcode', flex: 1, minWidth: 120 },
              { headerName: 'Region', field: 'region', flex: 1, minWidth: 120 },
              { headerName: 'Country', field: 'country', flex: 1, minWidth: 120 },
              { headerName: 'Latitude', field: 'lat', flex: 1, minWidth: 120 },
              { headerName: 'Longitude', field: 'lon', flex: 1, minWidth: 120 },
            ]}
            pageSize={20}
            floatingFilter={false}
            shouldShowPagination={true}
            loading={false}
            style={{ height: '100%', width: '100%' }}
          />
        </div>
      </Dialog>
    </LayoutBox>
  );
};

export default AggregationMapView;
